import {Injectable} from '@angular/core';
import {EMPTY, Observable, Subject} from 'rxjs';
import {expand, reduce} from 'rxjs/operators';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {AuthService} from './auth.service';
import {SubscriptionItem, SubscriptionQuery, SubscriptionRequest, SubscriptionsListResponse} from '../entities';

/** One day's polling counts for a single carrier (gap-filled across the window). */
export interface CarrierMetricSeriesPoint {
  date: string;
  attempts: number;
  success: number;
  errors: number;
}

/** Per-type error breakdown for a carrier over the selected window. */
export interface CarrierMetricErrors {
  auth: number;
  client4xx: number;
  server5xx: number;
  carrier: number;
  timeout: number;
  transform: number;
  invalidJson: number;
}

/** Aggregated polling health for a single carrier (SCAC) over the window. */
export interface CarrierMetric {
  scacCode: string;
  attempts: number;
  success: number;
  noData: number;
  errors: CarrierMetricErrors;
  successRate: number;
  state: 'ok' | 'warn' | 'fail';
  lastPolledAt: string;
  lastOutcome: string;
  series: CarrierMetricSeriesPoint[];
}

/** Response from GET /carrier/metrics — carriers sorted worst-first server-side. */
export interface CarrierMetricsResponse {
  window: number;
  carriers: CarrierMetric[];
}

/** Response from GET /carrier/credentials: the stored per-carrier credential values. */
export interface CarrierCredentialsResponse {
  env: string;
  carrierCredentials: Record<string, Record<string, string>>;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  private readonly baseUrl = environment.apiUrl;
  private readonly refresh$ = new Subject<void>();

  /** Fires when the subscription list should be reloaded (e.g. after API key change) */
  readonly refresh = this.refresh$.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
  }

  triggerRefresh(): void {
    this.refresh$.next();
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set('x-api-key', this.authService.getApiKey());
  }

  createSubscription(body: SubscriptionRequest): Observable<any> {
    const url = this.baseUrl + '/subscription/equipment';
    return this.http.post(url, body, {headers: this.getHeaders()});
  }

  /**
   * Fetch a single page of subscriptions. Passing `limit` and/or `nextToken`
   * opts into server-side pagination; omitting both returns every subscription
   * in one response (backend default).
   */
  getSubscriptions(query: SubscriptionQuery = {}): Observable<SubscriptionsListResponse> {
    const url = this.baseUrl + '/subscriptions';
    const params: any = {};
    if (query.status) params.subscriptionStatus = query.status;
    if (query.referencePrefix) params.referencePrefix = query.referencePrefix;
    if (query.limit != null) params.limit = String(query.limit);
    if (query.nextToken) params.nextToken = query.nextToken;
    return this.http.get<SubscriptionsListResponse>(url, {params, headers: this.getHeaders()});
  }

  /**
   * Fetch every matching subscription, transparently following the `nextToken`
   * cursor across pages. Keeps callers working with a complete list even if the
   * backend later caps responses to a page size.
   */
  private fetchAllPages(query: SubscriptionQuery, pageSize = 200): Observable<SubscriptionItem[]> {
    return this.getSubscriptions({...query, limit: pageSize}).pipe(
      expand(res => res.nextToken
        ? this.getSubscriptions({...query, limit: pageSize, nextToken: res.nextToken})
        : EMPTY),
      reduce((acc, res) => acc.concat(res.subscriptions || []), [] as SubscriptionItem[])
    );
  }

  getAllSubscriptions(status?: string): Observable<SubscriptionItem[]> {
    return this.fetchAllPages({status});
  }

  /**
   * Prefix search on the subscription reference (begins_with). Case-sensitive
   * and left-anchored; spans all statuses, so CLOSED/deleted subscriptions are
   * also returned.
   */
  searchByReference(referencePrefix: string): Observable<SubscriptionItem[]> {
    return this.fetchAllPages({referencePrefix});
  }

  deleteSubscription(subscriptionReference: string): Observable<any> {
    const url = this.baseUrl + '/subscription/' + encodeURIComponent(subscriptionReference);
    return this.http.delete(url, {headers: this.getHeaders()});
  }

  getWebhookConfig(): Observable<{ targetUrl: string; activated: boolean }> {
    const url = this.baseUrl + '/webhook/configure';
    return this.http.get<{ targetUrl: string; activated: boolean }>(url, {headers: this.getHeaders()});
  }

  configureWebhook(targetUrl: string): Observable<any> {
    const url = this.baseUrl + '/webhook/configure';
    return this.http.post(url, {targetUrl}, {headers: this.getHeaders()});
  }

  activateWebhook(secret: string): Observable<any> {
    const url = this.baseUrl + '/webhook/activate';
    return this.http.post(url, {secret}, {headers: this.getHeaders()});
  }

  getCarrierCredentials(): Observable<CarrierCredentialsResponse> {
    const url = this.baseUrl + '/carrier/credentials';
    return this.http.get<CarrierCredentialsResponse>(url, {headers: this.getHeaders()});
  }

  saveCarrierCredentials(carrierCredentials: Record<string, Record<string, string>>): Observable<any> {
    const url = this.baseUrl + '/carrier/credentials';
    return this.http.post(url, carrierCredentials, {headers: this.getHeaders()});
  }

  deleteCarrierCredentials(carriers: string[]): Observable<any> {
    const url = this.baseUrl + '/carrier/credentials';
    return this.http.delete(url, {headers: this.getHeaders(), body: {carriers}});
  }

  carrierHealthCheck(scacCode: string): Observable<{ scacCode: string; status: string; httpCode: number | null }> {
    const url = this.baseUrl + '/carrier/health-check';
    return this.http.post<{ scacCode: string; status: string; httpCode: number | null }>(url, {scacCode}, {headers: this.getHeaders()});
  }

  /** Dev-only per-client carrier polling metrics over the given rolling window (days). */
  getCarrierMetrics(windowDays: number): Observable<CarrierMetricsResponse> {
    const url = this.baseUrl + '/carrier/metrics?window=' + windowDays;
    return this.http.get<CarrierMetricsResponse>(url, {headers: this.getHeaders()});
  }
}
